import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, CheckCircle, Loader2 } from 'lucide-react';
import { GlassCard } from '@/components/ui/GlassCard';

export const ContactForm = () => {
  const [status, setStatus] = useState(''); // '', 'loading', 'success', 'error'

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');

    // Récupération des données du formulaire
    const formData = new FormData(e.target);
    
    // 🚨 REMPLACEZ LA CLÉ CI-DESSOUS PAR CELLE REÇUE PAR EMAIL 🚨
    formData.append("access_key", "c3e35a27-ac6f-4b36-a7cc-8983435b3ecf");

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setStatus('success');
        e.target.reset(); // On vide le formulaire
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="relative py-20 sm:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* En-tête de la section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Discutons de votre <span className="text-[#00e5ff]">Projet</span>
          </h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            Une question sur nos formations, un besoin sur mesure pour votre entreprise ou pour votre école ? Écrivez-nous.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          
          {/* Colonne 1 : Vos Informations de Contact */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex flex-col justify-center"
          >
            <GlassCard className="p-8 space-y-8">
              <h3 className="text-2xl font-semibold text-white mb-6">Nos Coordonnées</h3>
              
              {/* 🚨 REMPLACEZ PAR VOS VRAIS CONTACTS ICI 🚨 */}
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-full bg-[#00e5ff]/10 flex items-center justify-center shrink-0">
                  <Mail className="w-6 h-6 text-[#00e5ff]" />
                </div>
                <div>
                  <p className="text-white/50 text-sm">Email</p>
                  <p className="text-white text-lg font-medium">tech@monaliapro.com</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-full bg-[#00e5ff]/10 flex items-center justify-center shrink-0">
                  <Phone className="w-6 h-6 text-[#00e5ff]" />
                </div>
                <div>
                  <p className="text-white/50 text-sm">Téléphone / WhatsApp</p>
                  <p className="text-white text-lg font-medium">+228 90700624</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-full bg-[#00e5ff]/10 flex items-center justify-center shrink-0">
                  <MapPin className="w-6 h-6 text-[#00e5ff]" />
                </div>
                <div>
                  <p className="text-white/50 text-sm">Adresse</p>
                  <p className="text-white text-lg font-medium">Lomé, Togo<br/>(Togblékopé)</p>
                </div>
              </div>
            </GlassCard>
          </motion.div>

          {/* Colonne 2 : Le Formulaire interactif */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <GlassCard className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                
                {/* Antispam Botpoison (Invisible) */}
                <input type="checkbox" name="botcheck" className="hidden" style={{ display: 'none' }} />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-white/70 text-sm font-medium">Nom complet</label>
                    <input 
                      type="text" 
                      name="name" 
                      required 
                      className="w-full bg-[#0a0f1e] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#00e5ff] transition-colors"
                      placeholder="Jean Dupont"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-white/70 text-sm font-medium">Adresse Email</label>
                    <input 
                      type="email" 
                      name="email" 
                      required 
                      className="w-full bg-[#0a0f1e] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#00e5ff] transition-colors"
                      placeholder="jean@entreprise.com"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-white/70 text-sm font-medium">Sujet de votre message</label>
                  <select 
                    name="subject" 
                    required
                    className="w-full bg-[#0a0f1e] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#00e5ff] transition-colors appearance-none"
                  >
                    <option value="Demande d'information">Demande d'information</option>
                    <option value="Projet Sur Mesure">Projet Développement Sur Mesure</option>
                    <option value="École360">Intérêt pour École360</option>
                    <option value="Formation">Intérêt pour les Formations</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-white/70 text-sm font-medium">Votre Message</label>
                  <textarea 
                    name="message" 
                    required 
                    rows="4"
                    className="w-full bg-[#0a0f1e] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#00e5ff] transition-colors resize-none"
                    placeholder="Dites-nous en plus sur vos besoins..."
                  ></textarea>
                </div>

                {/* Bouton de soumission avec gestion d'état */}
                <button 
                  type="submit" 
                  disabled={status === 'loading'}
                  className="w-full bg-[#00e5ff]/10 hover:bg-[#00e5ff]/20 text-[#00e5ff] border border-[#00e5ff] font-semibold rounded-lg px-6 py-4 transition-all flex items-center justify-center space-x-2 hover:shadow-[0_0_20px_rgba(0,229,255,0.3)] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {status === 'loading' ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> <span>Envoi en cours...</span></>
                  ) : status === 'success' ? (
                    <><CheckCircle className="w-5 h-5" /> <span>Message Envoyé !</span></>
                  ) : (
                    <><Send className="w-5 h-5" /> <span>Envoyer le message</span></>
                  )}
                </button>

                {status === 'error' && (
                  <p className="text-red-400 text-sm text-center mt-2">
                    Oups, une erreur est survenue. Veuillez réessayer ou nous contacter directement par email.
                  </p>
                )}

              </form>
            </GlassCard>
          </motion.div>

        </div>
      </div>
    </section>
  );
};